import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { useSavedRecommendations, useRecentlyViewed, useRecommendedForYou } from '../hooks/useDiscovery';
import { RecommendationCard } from '../components/RecommendationCard';
import { formatDate } from '../utils/format';
import { CategorySelector, CitySelector } from '../components/reference-selectors';
import { useReferences } from '../hooks/useDiscovery';

export function ConsumerProfile() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const { saved, loading: savedLoading, unsave } = useSavedRecommendations();
  const { items: recentlyViewed, loading: recentlyViewedLoading } = useRecentlyViewed(6);
  const { result: recommendedForYou, loading: recommendedForYouLoading } = useRecommendedForYou(6);
  const { categories, cities, loading: refsLoading } = useReferences();

  if (loading) {
    return (
      <div className="container py-12">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="skeleton h-8 w-1/3" />
          <div className="skeleton h-32 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/auth?next=/profile');
    return null;
  }

  const initials = user.email
    .split('@')[0]
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Mi perfil</h1>
            <button
              type="button"
              onClick={logout}
              className="btn-secondary text-sm"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <main className="container py-12">
        <div className="max-w-2xl mx-auto">
          {/* Profile Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
                {initials}
              </div>
              <div className="text-center md:text-left flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{user.email}</h2>
                <p className="text-gray-500 mt-1 capitalize">{user.role} consumer</p>
                <p className="text-sm text-gray-400 mt-4">
                  Miembro desde {formatDate(user.id.includes('_') ? new Date().toISOString() : new Date().toISOString())}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Discovery Links */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Explorar</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/categories"
                className="card p-6 hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                  Explorar categorías
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {categories.length} categorías disponibles
                </p>
              </Link>
              <Link
                to="/cities"
                className="card p-6 hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                  Explorar ciudades
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {cities.length} ciudades disponibles
                </p>
              </Link>
            </div>
          </section>

          {/* Saved Recommendations */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Mis guardados ({saved.length})
              </h2>
            </div>

            {savedLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="card">
                    <div className="skeleton aspect-video w-full" />
                    <div className="p-4 space-y-3">
                      <div className="skeleton h-6 w-3/4" />
                      <div className="skeleton h-4 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!savedLoading && saved.length === 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No tienes recomendaciones guardadas</h3>
                <p className="mt-2 text-gray-500">Cuando guardes una recomendación, aparecerá aquí</p>
                <Link to="/" className="mt-6 inline-block btn-primary">
                  Explorar lugares
                </Link>
              </div>
            )}

            {!savedLoading && saved.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {saved.map((entry) =>
                  entry.recommendation ? (
                    <div key={entry.recommendation.id} className="relative group">
                      <RecommendationCard recommendation={entry.recommendation} />
                      <button
                        type="button"
                        onClick={() => unsave(entry.recommendationId)}
                        aria-label={`Quitar ${entry.recommendation.title} de guardados`}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-gray-900/70 text-white flex items-center justify-center hover:bg-gray-900 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ) : null
                )}
              </div>
            )}
          </section>

          <section className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Vistos recientemente</h2>
            </div>

            {recentlyViewedLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => <div key={i} className="skeleton h-48 rounded-xl" />)}
              </div>
            )}

            {!recentlyViewedLoading && recentlyViewed.length === 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
                Tus recomendaciones vistas aparecerán aquí.
              </div>
            )}

            {!recentlyViewedLoading && recentlyViewed.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentlyViewed.map((entry) => (
                  <RecommendationCard key={entry.recommendationId} recommendation={entry.recommendation} />
                ))}
              </div>
            )}
          </section>

          <section className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Recomendados para ti</h2>
            </div>

            {recommendedForYouLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => <div key={i} className="skeleton h-48 rounded-xl" />)}
              </div>
            )}

            {!recommendedForYouLoading && (recommendedForYou?.recommendations.length ?? 0) === 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
                Explora y guarda lugares para mejorar estas recomendaciones.
              </div>
            )}

            {!recommendedForYouLoading && (recommendedForYou?.recommendations.length ?? 0) > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {recommendedForYou!.recommendations.map((recommendation) => (
                  <RecommendationCard key={recommendation.id} recommendation={recommendation} />
                ))}
              </div>
            )}
          </section>

          {/* Quick Category/City Filters */}
          {!refsLoading && (categories.length > 0 || cities.length > 0) && (
            <section className="mt-12">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtrar mis guardados</h2>
              <div className="flex flex-wrap gap-4">
                <CategorySelector
                  selectedId={null}
                  onChange={() => {}}
                  categories={categories}
                  loading={refsLoading}
                  disabled={true}
                />
                <CitySelector
                  selectedId={null}
                  onChange={() => {}}
                  cities={cities}
                  loading={refsLoading}
                  disabled={true}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Visita <Link to="/categories" className="text-primary hover:underline">Categorías</Link> o <Link to="/cities" className="text-primary hover:underline">Ciudades</Link> para filtrar.
              </p>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
